import { matchPath } from 'react-router-dom';

/**
 * 路由元信息（pageId 用于埋点、background 用于页面底色、title 备用）。
 */
export interface RouteMeta {
  title?: string;
  pageId?: string;
  background?: string;
  /** 部分轻量页不需要 Container 默认撑满视口，可通过该开关关闭内部 min-height */
  containerMinHeight?: boolean;
  [key: string]: any;
}

interface RouteMetaEntry {
  path: string;
  exact?: boolean;
  strict?: boolean;
  meta?: RouteMeta;
}

/**
 * 为什么单独存在：`getRouteMetaInfo` 原本定义在 `router.tsx`，而 dataPort（埋点）、
 * BaseLayout、分页组件都要调用它做 pageId 查询。这会形成
 * `dataPort → router → (eager HomePage→Banner→dataPort)` 的循环依赖，
 * 在 HMR/生产 chunk 初始化时触发「Cannot access 'expReportSimple' before initialization」(TDZ)。
 *
 * 此文件只依赖 react-router-dom 的 matchPath，不引用任何业务组件与 dataPort，
 * 因此可被 dataPort 等底层模块安全引用，彻底断开依赖环。
 *
 * ⚠️ 维护约定：这是 pageId/title 查询用的「path + meta」镜像。`router.tsx` 的 `routes`
 * 与 `WorkBench.tsx` 仍负责「path + 组件」的实际渲染。新增/修改带 pageId、background 的
 * 路由时，需同步更新本表，顺序需与渲染路由的匹配优先级保持一致（通配 `*` 必须放最后）。
 */
const routeMetaList: RouteMetaEntry[] = [
  // ===== 主路由（与 router.tsx routes 对应）=====
  { path: '/page/list', exact: true, meta: { title: 'searchList', pageId: 'ProductList' } },
  { path: '/', exact: true, meta: { title: 'home', pageId: 'HomePage' } },
  { path: '/homepagePreview', exact: true, meta: { title: 'homePreview', pageId: 'HomePage', simplePage: true } },
  { path: '/activityPagePreview', exact: true, meta: { title: 'activityPagePreview', pageId: 'ActivityPage', simplePage: true } },
  { path: '/select', exact: true, meta: { title: 'select', pageId: 'SelectList' } },
  { path: '/sku/:skuId', exact: true, meta: { title: 'skuDetail', pageId: 'ProductDetail' } },
  { path: '/cart/carts', exact: true, meta: { title: 'cartList', background: '#F3F6F9', pageId: 'Cart' } },
  { path: '/cart/batchAdd', exact: true, meta: { title: 'batchCarts', pageId: 'BatchAddCart' } },
  { path: '/order/goOrderInfo', exact: true, meta: { title: 'settlement', pageId: 'Checkout' } },
  { path: '/about', exact: true, meta: { title: 'about', pageId: 'about' } },
  { path: '/afterSalesPolicy', exact: true, meta: { title: 'afterSalesPolicy', pageId: 'AfterSalesPolicy' } },
  { path: '/searchAi', exact: true, meta: { title: 'searchAi', background: '#fff', pageId: 'SearchAi' } },
  { path: '/rede/searchAi', exact: true, meta: { title: 'searchAi', background: '#fff', pageId: 'SearchAi' } },
  {
    path: '/rede/searchAi/productDetail',
    exact: true,
    meta: { title: 'skuDetail', background: '#fff', pageId: 'SearchAiProductDetail', simplePage: true },
  },
  {
    path: '/rede/searchAi/matchList',
    exact: true,
    meta: { title: 'searchAiMatchList', background: '#fff', pageId: 'SearchAiMatchList', simplePage: true },
  },

  // ===== 工作台路由（与 WorkBench.tsx 对应）=====
  { path: '/orderList', exact: true, meta: { title: 'orderList', pageId: 'OrderList' } },
  { path: '/intentionList', exact: true, meta: { title: 'intentionList', pageId: 'IntentionList' } },
  { path: '/intention/detail', exact: true, meta: { title: 'intentionDetail', pageId: 'IntentionDetail' } },
  { path: '/Approval', exact: true, meta: { title: 'Approval', pageId: 'Approval' } },
  { path: '/settleConfig', exact: true, meta: { title: 'settleConfig', pageId: 'SettleConfig' } },
  { path: '/approvalList', exact: true, meta: { title: 'approvalList', pageId: 'ApprovalList' } },
  { path: '/addressList', exact: true, meta: { title: 'addressList', pageId: 'AddAddress' } },
  { path: '/authorityAddress', exact: true, meta: { title: 'authorityAddress', pageId: 'AddressAuthority' } },
  { path: '/materialManage', exact: true, meta: { title: 'materialCode', pageId: 'MaterialManagement' } },
  // 回收站 / 批次列表复用订单列表组件，单独 pageId，避免点击被算进普通订单列表
  { path: '/deleteOrderList', exact: true, meta: { title: '订单回收站', pageId: 'DeleteOrderList' } },
  { path: '/batchList', exact: true, meta: { title: 'orderDetail', pageId: 'BatchOrderList' } },
  { path: '/order/detail', exact: true, meta: { title: 'orderDetail', pageId: 'OrderDetail' } },
  { path: '/accountSettings', exact: true, meta: { title: 'account', pageId: 'AccountManagement' } },
  { path: '/bindingEmail', exact: true, meta: { title: '绑定邮箱', pageId: 'BindingEmail' } },
  { path: '/processList', exact: true, meta: { title: 'approvalSettings', pageId: 'ProcessList' } },
  { path: '/editProcess', exact: true, meta: { title: 'editApprovalFlow', pageId: 'EditProcess' } },
  { path: '/createProcess', exact: true, meta: { title: 'createApprovalFlow', pageId: 'CreateProcess' } },
  { path: '/taskCenter', exact: true, meta: { title: 'taskCenter', pageId: 'TaskCenter' } },
  { path: '/afterSale/apply', exact: true, meta: { title: 'AfterSale', pageId: 'AfterSaleApply' } },
  { path: '/afterSale/detail', exact: true, meta: { title: 'AfterSale', pageId: 'AfterSaleDetail' } },
  { path: '/activityPage', exact: true, meta: { title: 'activityPage', pageId: 'ActivityPage' } },
  // 客户登记此前误挂 ActivityPage，自定义埋点会串到活动页；该路由没有历史自定义事件
  { path: '/customerregistration', exact: true, meta: { title: 'CustomerRegistration', pageId: 'CustomerRegistration' } },
  { path: '/remittance', exact: true, meta: { title: 'remittance', pageId: 'Remittance' } },

  // 公开页不在 routes 数组里，但仍要能解析 pageId，否则登录/注册点击会被丢掉
  { path: '/passport/login', exact: true, meta: { title: 'login', pageId: 'Login' } },
  { path: '/register/guide', exact: true, meta: { title: 'RegisterGuide', pageId: 'RegisterGuide' } },
  { path: '/register', exact: true, meta: { title: 'Register', pageId: 'Register' } },
  { path: '/register/success', exact: true, meta: { title: 'RegisterSuccess', pageId: 'RegisterSuccess' } },

  // ===== 兜底路由 =====
  // 没有 pageId 时 customClickReportSimple 会丢弃失效页的点击。
  { path: '/skuUnavailable', exact: true, meta: { title: 'skuUnavailable', containerMinHeight: false, pageId: 'SkuUnavailable' } },
  { path: '/403', exact: true, meta: { title: 'forbidden403', pageId: 'Forbidden403' } },
  { path: '*', exact: true, meta: { title: 'notFound', containerMinHeight: false, pageId: 'NotFound' } },
];

/**
 * 按路由匹配优先级返回当前 pathname 命中的路由 meta，未命中返回 null。
 * 行为与原 router.tsx 内实现一致（首个 matchPath 命中即返回）。
 */
export function getRouteMetaInfo(pathname: string): RouteMeta | null {
  const matchedRoute = routeMetaList.find(route => {
    const match = matchPath(pathname, {
      path: route.path,
      exact: route.exact,
      strict: route.strict,
    });
    return match !== null;
  });

  return matchedRoute && matchedRoute.meta ? matchedRoute.meta : null;
}
